import React from 'react';
import { Activity, ChevronDown, ChevronRight, Download, Filter, MessageCircle, Radio, RefreshCw, Search, X } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageShell from '../components/PageShell';
import { createAuthHeaders, cpFetch } from '../lib/cpClient';
import { useAuthStore } from '../store/authStore';
import {
  buildConversationExportPath,
  buildConversationPath,
  buildEventsExportPath,
  buildFilterParams,
  defaultFilters,
  type PulsarFilters,
  type PulsarStream,
} from './pulsarHelpers';
import { countPulsarOutcomes, formatPulsarSuccessRate } from './pulsarOutcomes';

type PulsarEvent = {
  item_id: string;
  timestamp: string;
  stream: PulsarStream | 'agent' | 'iot' | 'api';
  tenant_id: string;
  app_id?: string | null;
  request_id?: string | null;
  agent_id?: string | null;
  workflow?: string | null;
  tool_name?: string | null;
  device_id?: string | null;
  api_id?: string | null;
  endpoint_id?: string | null;
  provider_key?: string | null;
  success?: boolean | null;
  error?: string | null;
  duration_ms?: number | null;
  event_name?: string | null;
  source?: string | null;
  message_preview?: string | null;
  input_summary?: string | null;
  output_summary?: string | null;
};

type PulsarEventsResp = {
  ok: boolean;
  tenant_id: string;
  took_ms: number;
  limit: number;
  offset: number;
  has_more: boolean;
  items: PulsarEvent[];
};

type PulsarConversationResp = {
  ok: boolean;
  tenant_id: string;
  request_id: string;
  count: number;
  items: PulsarEvent[];
};

type PulsarInsightsResp = {
  ok: boolean;
  tenant_id: string;
  sampled: boolean;
  sample_size: number;
  summary: {
    total: number;
    success: number;
    failed: number;
    success_rate: number;
    avg_duration_ms: number;
  };
  stream_breakdown: Array<{ stream: string; count: number }>;
  top_tools: Array<{ name: string; count: number }>;
  top_agents: Array<{ name: string; count: number }>;
  top_workflows: Array<{ name: string; count: number }>;
  top_apis: Array<{ name: string; count: number }>;
  top_devices: Array<{ name: string; count: number }>;
};

async function downloadWithAuth(path: string, token: string, tenant: string, suggestedName: string) {
  const headers = new Headers(createAuthHeaders(token, tenant));
  const appId = (() => {
    try {
      const raw = localStorage.getItem('dc.auth');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.app_id || parsed?.appId || localStorage.getItem('dc.app_id') || null;
    } catch {
      return null;
    }
  })();
  headers.set('x-app-id', appId);

  const res = await fetch(path, { method: 'GET', headers, credentials: 'omit' });
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(`Export failed (${res.status})${t ? `: ${t.slice(0, 120)}` : ''}`);
  }
  const blob = await res.blob();
  const a = document.createElement('a');
  const url = URL.createObjectURL(blob);
  a.href = url;
  a.download = suggestedName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const VALID_STREAMS = new Set<PulsarStream>(['all', 'agent', 'iot', 'api']);

export default function PulsarPage() {
  const { token, selectedTenant } = useAuthStore();
  const authReady = Boolean(token && selectedTenant);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [filters, setFilters] = React.useState<PulsarFilters>(() => {
    const f = defaultFilters();
    const urlStream = searchParams.get('stream') as PulsarStream | null;
    if (urlStream && VALID_STREAMS.has(urlStream)) f.stream = urlStream;
    return f;
  });
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const [resp, setResp] = React.useState<PulsarEventsResp | null>(null);

  const [insightsLoading, setInsightsLoading] = React.useState(false);
  const [insightsErr, setInsightsErr] = React.useState<string | null>(null);
  const [insights, setInsights] = React.useState<PulsarInsightsResp | null>(null);

  const [convLoading, setConvLoading] = React.useState(false);
  const [convErr, setConvErr] = React.useState<string | null>(null);
  const [conv, setConv] = React.useState<PulsarConversationResp | null>(null);

  const [viewMode, setViewMode] = React.useState<'logs' | 'stories'>('stories');
  const [expandedRows, setExpandedRows] = React.useState<Set<string>>(new Set());
  function toggleRow(id: string) {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function runSearch(offset = 0) {
    if (!authReady) return;
    setLoading(true);
    setErr(null);
    try {
      const qs = buildFilterParams(filters, offset);
      const data = await cpFetch(`/pulsar/events?${qs}`);
      setResp(data as PulsarEventsResp);
    } catch (e: any) {
      setErr(e?.message || String(e));
      setResp(null);
    } finally {
      setLoading(false);
    }
  }

  async function runInsights() {
    if (!authReady) return;
    setInsightsLoading(true);
    setInsightsErr(null);
    try {
      const params = new URLSearchParams(buildFilterParams({ ...filters, limit: Math.min(filters.limit, 100) }, 0));
      params.set('sample_limit', '500');
      const data = await cpFetch(`/pulsar/insights?${params.toString()}`);
      setInsights(data as PulsarInsightsResp);
    } catch (e: any) {
      setInsightsErr(e?.message || String(e));
      setInsights(null);
    } finally {
      setInsightsLoading(false);
    }
  }

  async function openConversation(requestId: string) {
    if (!authReady || !requestId) return;
    setConvLoading(true);
    setConvErr(null);
    try {
      const data = await cpFetch(buildConversationPath(requestId, 500));
      setConv(data as PulsarConversationResp);
    } catch (e: any) {
      setConvErr(e?.message || String(e));
      setConv(null);
    } finally {
      setConvLoading(false);
    }
  }

  async function exportEvents(format: 'json' | 'csv') {
    if (!authReady || !token || !selectedTenant) return;
    const file = `pulsar-events-${new Date().toISOString().replace(/[:.]/g, '-')}.${format === 'csv' ? 'csv' : 'json'}`;
    await downloadWithAuth(buildEventsExportPath(filters, format), token, selectedTenant, file);
  }

  async function exportConversation(format: 'json' | 'csv') {
    if (!authReady || !token || !selectedTenant || !conv?.request_id) return;
    const file = `pulsar-conversation-${conv.request_id}.${format === 'csv' ? 'csv' : 'json'}`;
    await downloadWithAuth(
      buildConversationExportPath(conv.request_id, format),
      token,
      selectedTenant,
      file
    );
  }

  function openExecutionDetails(requestId?: string | null, agentId?: string | null) {
    if (requestId) {
      navigate(`/agents/observe?request_id=${encodeURIComponent(String(requestId))}`);
      return;
    }
    if (agentId) {
      navigate(`/agents/hub?agent=${encodeURIComponent(String(agentId))}`);
    }
  }

  const listedOutcomes = countPulsarOutcomes(resp?.items);
  const insightsSummary = insights?.summary;
  const quickStat = listedOutcomes.total > 0
    ? {
        total: listedOutcomes.total,
        success: listedOutcomes.success,
        failed: listedOutcomes.failed,
        unclassified: listedOutcomes.unclassified,
        success_rate: listedOutcomes.success_rate,
        avg_duration_ms: insightsSummary?.avg_duration_ms ?? 0,
      }
    : {
        total: insightsSummary?.total ?? 0,
        success: insightsSummary?.success ?? 0,
        failed: insightsSummary?.failed ?? 0,
        unclassified: 0,
        success_rate: insightsSummary && (insightsSummary.success + insightsSummary.failed) > 0
          ? insightsSummary.success_rate
          : null,
        avg_duration_ms: insightsSummary?.avg_duration_ms ?? 0,
      };

  React.useEffect(() => {
    if (!authReady) return;
    runSearch(0);
    runInsights();
  }, [authReady]);

  return (
    <PageShell
      title="Pulsar"
      subtitle="Unified observability across agent conversations, IoT telemetry, and API ingest streams."
      icon={Radio}
      iconColor="bg-indigo-600"
    >
      <div className="space-y-4">
        <section className="rounded-xl border bg-white p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Filter className="h-4 w-4" />
                Unified Filters
              </div>
              <div className="flex bg-gray-100 p-0.5 rounded-lg border">
                <button
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === 'stories' ? 'bg-white shadow-sm text-indigo-700' : 'text-gray-500 hover:text-gray-700'}`}
                  onClick={() => setViewMode('stories')}
                >
                  Stories View
                </button>
                <button
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === 'logs' ? 'bg-white shadow-sm text-indigo-700' : 'text-gray-500 hover:text-gray-700'}`}
                  onClick={() => setViewMode('logs')}
                >
                  Raw Logs
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                className="inline-flex items-center gap-2 border rounded px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
                disabled={!authReady || loading}
                onClick={() => runSearch(0)}
              >
                <Search className="h-4 w-4" />
                {loading ? 'Searching...' : 'Search'}
              </button>
              <button
                className="inline-flex items-center gap-2 border rounded px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
                disabled={!authReady || insightsLoading}
                onClick={runInsights}
              >
                <Activity className="h-4 w-4" />
                {insightsLoading ? 'Loading...' : 'Insights'}
              </button>
              <button
                className="inline-flex items-center gap-2 border rounded px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
                disabled={!authReady}
                onClick={() => {
                  setFilters(defaultFilters());
                  setResp(null);
                  setInsights(null);
                  setConv(null);
                }}
              >
                <RefreshCw className="h-4 w-4" />
                Reset
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <Field label="Stream">
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={filters.stream}
                onChange={(e) => setFilters((f) => ({ ...f, stream: e.target.value as PulsarStream }))}
              >
                <option value="all">all</option>
                <option value="agent">agent</option>
                <option value="iot">iot</option>
                <option value="api">api</option>
              </select>
            </Field>
            <Field label="Query">
              <input className="w-full border rounded px-3 py-2 text-sm" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} />
            </Field>
            <Field label="App ID">
              <input className="w-full border rounded px-3 py-2 text-sm" value={filters.app_id} onChange={(e) => setFilters((f) => ({ ...f, app_id: e.target.value }))} />
            </Field>
            <Field label="Request ID">
              <input className="w-full border rounded px-3 py-2 text-sm" value={filters.request_id} onChange={(e) => setFilters((f) => ({ ...f, request_id: e.target.value }))} />
            </Field>
            <Field label="Agent ID">
              <input className="w-full border rounded px-3 py-2 text-sm" value={filters.agent_id} onChange={(e) => setFilters((f) => ({ ...f, agent_id: e.target.value }))} />
            </Field>
            <Field label="Workflow">
              <input className="w-full border rounded px-3 py-2 text-sm" value={filters.workflow} onChange={(e) => setFilters((f) => ({ ...f, workflow: e.target.value }))} />
            </Field>
            <Field label="Tool">
              <input className="w-full border rounded px-3 py-2 text-sm" value={filters.tool_name} onChange={(e) => setFilters((f) => ({ ...f, tool_name: e.target.value }))} />
            </Field>
            <Field label="Device ID">
              <input className="w-full border rounded px-3 py-2 text-sm" value={filters.device_id} onChange={(e) => setFilters((f) => ({ ...f, device_id: e.target.value }))} />
            </Field>
            <Field label="API ID">
              <input className="w-full border rounded px-3 py-2 text-sm" value={filters.api_id} onChange={(e) => setFilters((f) => ({ ...f, api_id: e.target.value }))} />
            </Field>
            <Field label="Endpoint ID">
              <input className="w-full border rounded px-3 py-2 text-sm" value={filters.endpoint_id} onChange={(e) => setFilters((f) => ({ ...f, endpoint_id: e.target.value }))} />
            </Field>
            <Field label="Provider">
              <input className="w-full border rounded px-3 py-2 text-sm" value={filters.provider_key} onChange={(e) => setFilters((f) => ({ ...f, provider_key: e.target.value }))} />
            </Field>
            <Field label="Limit">
              <input
                className="w-full border rounded px-3 py-2 text-sm"
                value={String(filters.limit)}
                onChange={(e) => setFilters((f) => ({ ...f, limit: Number(e.target.value || 50) }))}
              />
            </Field>
            <Field label="Since">
              <input className="w-full border rounded px-3 py-2 text-sm" type="datetime-local" value={filters.since} onChange={(e) => setFilters((f) => ({ ...f, since: e.target.value }))} />
            </Field>
            <Field label="Until">
              <input className="w-full border rounded px-3 py-2 text-sm" type="datetime-local" value={filters.until} onChange={(e) => setFilters((f) => ({ ...f, until: e.target.value }))} />
            </Field>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="inline-flex items-center gap-2 border rounded px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
              disabled={!authReady}
              onClick={() => exportEvents('json')}
            >
              <Download className="h-4 w-4" />
              Export JSON
            </button>
            <button
              className="inline-flex items-center gap-2 border rounded px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
              disabled={!authReady}
              onClick={() => exportEvents('csv')}
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
            <span className="text-xs text-gray-500">
              {resp ? `took ${resp.took_ms}ms • rows ${resp.items.length}` : 'Run search to load events'}
            </span>
          </div>
          {err ? <div className="text-sm text-red-600">Error: {err}</div> : null}
          {insightsErr ? <div className="text-sm text-red-600">Insights error: {insightsErr}</div> : null}
        </section>

        <section className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Card label="Events" value={quickStat.total} />
          <Card label="Success" value={quickStat.success} />
          <Card label="Failed" value={quickStat.failed} />
          <Card label="Unclassified" value={quickStat.unclassified} />
          <Card label="Success Rate" value={formatPulsarSuccessRate(quickStat.success_rate)} />
          <Card label="Avg Duration" value={`${quickStat.avg_duration_ms}ms`} />
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <SimpleList title="Top Tools" items={insights?.top_tools || []} />
          <SimpleList title="Top Agents" items={insights?.top_agents || []} />
          <SimpleList title="Top Workflows" items={insights?.top_workflows || []} />
          <SimpleList title="Top APIs" items={insights?.top_apis || []} />
          <SimpleList title="Top Devices" items={insights?.top_devices || []} />
          <SimpleList title="Stream Breakdown" items={insights?.stream_breakdown?.map((x) => ({ name: x.stream, count: x.count })) || []} />
        </section>

        {viewMode === 'logs' ? (
          <section className="rounded-xl border bg-white overflow-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 text-gray-600">
                <tr className="text-left">
                  <th className="py-2 px-3 w-6"></th>
                  <th className="py-2 px-3">time</th>
                  <th className="py-2 px-3">stream</th>
                  <th className="py-2 px-3">event</th>
                  <th className="py-2 px-3">app/device/api</th>
                  <th className="py-2 px-3">agent/workflow</th>
                  <th className="py-2 px-3">ok</th>
                  <th className="py-2 px-3">duration</th>
                  <th className="py-2 px-3">chat</th>
                </tr>
              </thead>
              <tbody>
                {(resp?.items || []).map((row) => {
                  const rowKey = `${row.stream}:${row.item_id}:${row.timestamp}`;
                  const hasContent = !!(row.input_summary || row.output_summary);
                  const isExpanded = expandedRows.has(rowKey);
                  return (
                    <React.Fragment key={rowKey}>
                      <tr
                        className="border-t hover:bg-gray-50 cursor-pointer"
                        onClick={() => hasContent ? toggleRow(rowKey) : (row.request_id ? openConversation(String(row.request_id)) : undefined)}
                        title={hasContent ? 'Click to expand chat content' : (row.request_id ? 'Click to open conversation thread' : '')}
                      >
                        <td className="py-2 px-3 text-gray-400">
                          {hasContent ? (isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />) : null}
                        </td>
                        <td className="py-2 px-3 font-mono whitespace-nowrap">{new Date(row.timestamp).toLocaleString()}</td>
                        <td className="py-2 px-3 font-mono">{row.stream}</td>
                        <td className="py-2 px-3 font-mono max-w-[240px] truncate">{row.event_name || '—'}</td>
                        <td className="py-2 px-3 font-mono max-w-[220px] truncate">{row.app_id || row.device_id || row.api_id || '—'}</td>
                        <td className="py-2 px-3 font-mono max-w-[260px] truncate">{row.agent_id || row.workflow || '—'}</td>
                        <td className="py-2 px-3">{row.success === true ? 'true' : row.success === false ? 'false' : '—'}</td>
                        <td className="py-2 px-3">{typeof row.duration_ms === 'number' ? `${row.duration_ms}ms` : '—'}</td>
                        <td className="py-2 px-3">
                          {hasContent ? (
                            <span className="inline-flex items-center gap-1 text-indigo-600">
                              <MessageCircle className="h-3.5 w-3.5" />
                              <span className="text-[10px]">view</span>
                            </span>
                          ) : '—'}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-gray-50/50">
                          <td colSpan={9} className="p-0">
                            <ChatBubbles input={row.input_summary} output={row.output_summary} error={row.error} agentId={row.agent_id} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {!loading && (!resp?.items || resp.items.length === 0) ? (
                  <tr><td className="py-6 px-3 text-sm text-gray-500" colSpan={9}>No events found for the selected filters.</td></tr>
                ) : null}
              </tbody>
            </table>
            <div className="flex items-center gap-2 p-3 border-t bg-gray-50">
              <button
                className="border rounded px-3 py-1.5 text-xs hover:bg-white disabled:opacity-50"
                disabled={!resp?.has_more || loading}
                onClick={() => runSearch((resp?.offset || 0) + (resp?.limit || 0))}
              >
                Next Page
              </button>
              <span className="text-xs text-gray-500">offset {resp?.offset || 0} · limit {resp?.limit || filters.limit}</span>
            </div>
          </section>
        ) : (
          <StoriesView
            items={resp?.items || []}
            loading={loading}
            onLoadMore={() => runSearch((resp?.offset || 0) + (resp?.limit || 0))}
            hasMore={resp?.has_more || false}
            offset={resp?.offset || 0}
            limit={filters.limit}
            onOpenExecutionDetails={openExecutionDetails}
          />
        )}

        {convLoading ? <div className="text-sm text-gray-500">Loading conversation...</div> : null}
        {convErr ? <div className="text-sm text-red-600">Conversation error: {convErr}</div> : null}

        {conv ? (
          <section className="rounded-xl border bg-white p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-gray-900">
                Conversation <span className="font-mono text-sm">{conv.request_id}</span>
              </div>
              <div className="flex items-center gap-2">
                <button className="inline-flex items-center gap-2 border rounded px-3 py-1.5 text-xs hover:bg-gray-50" onClick={() => exportConversation('json')}>
                  <Download className="h-3.5 w-3.5" />
                  Export JSON
                </button>
                <button className="inline-flex items-center gap-2 border rounded px-3 py-1.5 text-xs hover:bg-gray-50" onClick={() => exportConversation('csv')}>
                  <Download className="h-3.5 w-3.5" />
                  Export CSV
                </button>
                <button className="inline-flex items-center gap-1 border rounded px-3 py-1.5 text-xs hover:bg-gray-50" onClick={() => setConv(null)}>
                  <X className="h-3.5 w-3.5" />
                  Close
                </button>
              </div>
            </div>
            <div className="space-y-2">
              {conv.items.map((e) => (
                <div key={`${e.timestamp}:${e.item_id}`} className="rounded border p-3 space-y-2">
                  <div className="flex flex-wrap gap-3 text-xs text-gray-600">
                    <span className="font-mono">{new Date(e.timestamp).toLocaleString()}</span>
                    <span className="font-mono">{e.stream}</span>
                    <span className="font-mono">{e.event_name || '—'}</span>
                    <span className="font-mono">{e.agent_id || '—'}</span>
                    <span className="font-mono">{e.workflow || '—'}</span>
                    {typeof e.duration_ms === 'number' ? <span>{e.duration_ms}ms</span> : null}
                    {e.success === true ? <span className="text-green-700">ok</span> : e.success === false ? <span className="text-red-700">failed</span> : null}
                  </div>
                  <ChatBubbles input={e.input_summary} output={e.output_summary} error={e.error} agentId={e.agent_id} />
                </div>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </PageShell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-xs text-gray-600">{label}</div>
      {children}
    </div>
  );
}

function Card({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl border bg-white p-3">
      <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className="text-xl font-semibold text-gray-900 mt-1">{value}</div>
    </div>
  );
}

function ChatBubbles({
  input,
  output,
  error,
  agentId,
}: {
  input?: string | null;
  output?: string | null;
  error?: string | null;
  agentId?: string | null;
}) {
  if (!input && !output && !error) return null;
  return (
    <div className="px-4 py-3 space-y-2 max-w-3xl">
      {input ? (
        <div className="flex justify-end">
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl rounded-tr-sm px-3 py-2 max-w-[90%]">
            <div className="text-[10px] font-medium text-indigo-500 mb-0.5">User</div>
            <div className="text-xs text-gray-800 whitespace-pre-wrap break-words">{input}</div>
          </div>
        </div>
      ) : null}
      {output ? (
        <div className="flex justify-start">
          <div className="bg-white border border-gray-200 rounded-xl rounded-tl-sm px-3 py-2 max-w-[90%]">
            <div className="text-[10px] font-medium text-gray-500 mb-0.5">{agentId || 'Agent'}</div>
            <div className="text-xs text-gray-800 whitespace-pre-wrap break-words">{output}</div>
          </div>
        </div>
      ) : null}
      {error ? (
        <div className="flex justify-start">
          <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 max-w-[90%]">
            <div className="text-[10px] font-medium text-red-500 mb-0.5">Error</div>
            <div className="text-xs text-red-700 whitespace-pre-wrap break-words">{error}</div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SimpleList({ title, items }: { title: string; items: Array<{ name: string; count: number }> }) {
  return (
    <div className="rounded-xl border bg-white p-3">
      <div className="text-sm font-medium text-gray-800 mb-2">{title}</div>
      {items.length === 0 ? (
        <div className="text-xs text-gray-500">No data</div>
      ) : (
        <div className="space-y-1">
          {items.slice(0, 8).map((item) => (
            <div key={`${title}:${item.name}`} className="flex items-center justify-between text-xs">
              <span className="font-mono text-gray-700 truncate pr-2">{item.name || 'unknown'}</span>
              <span className="text-gray-500">{item.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Stories View ─────────────────────────────────────────────────────────────

function StoriesView({ items, loading, onLoadMore, hasMore, offset, limit, onOpenExecutionDetails }: {
  items: PulsarEvent[], 
  loading: boolean, 
  onLoadMore: () => void,
  hasMore: boolean,
  offset: number,
  limit: number,
  onOpenExecutionDetails: (requestId?: string | null, agentId?: string | null) => void
}) {
  const groups = React.useMemo(() => {
    const map = new Map<string, PulsarEvent[]>();
    for (const item of items) {
      const key = item.request_id || `no-req-${item.item_id}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    }
    const summarizeConversationTitle = (events: PulsarEvent[]) => {
      const firstInput = events.find((e) => String(e.input_summary || '').trim())?.input_summary || '';
      const firstOutput = events.find((e) => String(e.output_summary || '').trim())?.output_summary || '';
      const primaryAgent = events.find((e) => String(e.agent_id || '').trim())?.agent_id || 'Agent';

      function parseTopicText(raw: string): string {
        const text = String(raw || '').trim();
        if (!text) return '';
        if (text.startsWith('{') || text.startsWith('[')) {
          try {
            const parsed = JSON.parse(text);
            if (parsed && typeof parsed === 'object') {
              const topic = parsed.topic || parsed.title || parsed.query || parsed.prompt || parsed.input || parsed.text;
              if (typeof topic === 'string' && topic.trim()) return topic.trim();
              const extracted = parsed.extracted_text;
              if (typeof extracted === 'string' && extracted.trim()) return extracted.trim();
              const file = parsed.filename;
              if (typeof file === 'string' && file.trim()) return `Document: ${file.trim()}`;
            }
          } catch {
            // Non-JSON payload; continue with plain text handling.
          }
        }
        return text;
      }

      const sourceText = parseTopicText(firstInput) || parseTopicText(firstOutput);
      const compact = sourceText.replace(/\s+/g, ' ').trim();
      if (!compact) return `${primaryAgent} conversation`;
      const maxLen = 82;
      return compact.length > maxLen ? `${compact.slice(0, maxLen - 1)}…` : compact;
    };

    const summarizePipelineTitle = (events: PulsarEvent[]) => {
      const apiId = events.find((e) => String(e.api_id || '').trim())?.api_id || null;
      const deviceId = events.find((e) => String(e.device_id || '').trim())?.device_id || null;
      if (apiId) return `API pipeline: ${apiId}`;
      if (deviceId) return `Device pipeline: ${deviceId}`;
      return 'Data pipeline';
    };

    // Sort groups by the newest event in each group
    return Array.from(map.entries())
      .map(([id, evts]) => ({
        id,
        events: evts.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()),
        newestTimestamp: Math.max(...evts.map(e => new Date(e.timestamp).getTime())),
        type: evts[0].stream === 'agent' ? 'conversation' : 'pipeline',
        title: evts[0].stream === 'agent' ? summarizeConversationTitle(evts) : summarizePipelineTitle(evts),
      }))
      .sort((a, b) => b.newestTimestamp - a.newestTimestamp);
  }, [items]);
  const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(new Set());
  const toggleGroup = React.useCallback((groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  // Stories default collapsed; auto-expand agent threads so chat bubbles are visible on first load.
  React.useEffect(() => {
    if (items.length === 0) return;
    setExpandedGroups((prev) => {
      if (prev.size > 0) return prev;
      const next = new Set<string>();
      for (const item of items) {
        if (item.stream === 'agent' && item.request_id) next.add(item.request_id);
      }
      if (next.size === 0) {
        const first = items[0];
        next.add(first.request_id || `no-req-${first.item_id}`);
      }
      return next;
    });
  }, [items]);

  if (loading && items.length === 0) return <div className="p-8 text-center text-gray-500">Loading stories...</div>;
  if (items.length === 0) return <div className="p-8 text-center text-gray-500 bg-white border rounded-xl">No events found for the selected filters.</div>;

  return (
    <div className="space-y-6">
      {groups.map(g => (
        <div key={g.id} className="bg-white border rounded-xl overflow-hidden shadow-sm">
          <div className="bg-gray-50/50 px-4 py-3 border-b flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="p-1 rounded hover:bg-gray-200/70 text-gray-600"
                onClick={() => toggleGroup(g.id)}
                aria-label={expandedGroups.has(g.id) ? 'Collapse conversation' : 'Expand conversation'}
              >
                {expandedGroups.has(g.id) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
              {g.type === 'conversation' ? (
                <div className="p-1.5 bg-indigo-100 text-indigo-700 rounded-md">
                  <MessageCircle className="h-4 w-4" />
                </div>
              ) : (
                <div className="p-1.5 bg-emerald-100 text-emerald-700 rounded-md">
                  <Activity className="h-4 w-4" />
                </div>
              )}
              <div>
                <div className="font-medium text-sm text-gray-900">
                  {g.title}
                </div>
                <div className="text-xs text-gray-500 font-mono">
                  {g.id.startsWith('no-req-') ? 'Isolated Event' : g.id} • {new Date(g.newestTimestamp).toLocaleString()}
                </div>
              </div>
            </div>
            <div className="text-xs text-gray-500">
              {g.events.length} event{g.events.length !== 1 ? 's' : ''}
            </div>
          </div>
          
          {expandedGroups.has(g.id) ? (
            <div className="p-4">
              {g.type === 'conversation' ? (
                <ConversationStory events={g.events} onOpenExecutionDetails={onOpenExecutionDetails} />
              ) : (
                <PipelineStory events={g.events} />
              )}
            </div>
          ) : null}
        </div>
      ))}
      
      <div className="flex items-center gap-2 p-3 border rounded-xl bg-white">
        <button
          className="border rounded px-4 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
          disabled={!hasMore || loading}
          onClick={onLoadMore}
        >
          {loading ? 'Loading...' : 'Load Older'}
        </button>
        <span className="text-xs text-gray-500">offset {offset} · limit {limit}</span>
      </div>
    </div>
  );
}

function parseStructuredOutput(output?: string | null): Record<string, any> | null {
  if (!output || typeof output !== 'string') return null;
  const trimmed = output.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;
  try {
    const parsed = JSON.parse(trimmed);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function ConversationStory({
  events,
  onOpenExecutionDetails,
}: {
  events: PulsarEvent[];
  onOpenExecutionDetails: (requestId?: string | null, agentId?: string | null) => void;
}) {
  return (
    <div className="space-y-4">
      {events.map((e, idx) => (
        <div key={e.item_id} className="relative">
          {idx > 0 && <div className="absolute top-0 left-6 -mt-4 bottom-full w-px bg-gray-200" />}
          
          {/* User Input Bubble */}
          {e.input_summary && (
            <div className="flex justify-end mb-4">
              <div className="max-w-[85%] bg-indigo-50 border border-indigo-100 text-indigo-900 px-4 py-3 rounded-2xl rounded-tr-sm shadow-sm">
                <div className="text-[10px] font-semibold text-indigo-500 mb-1 uppercase tracking-wider">User</div>
                <div className="text-sm whitespace-pre-wrap">{e.input_summary}</div>
              </div>
            </div>
          )}
          
          {/* Agent Output Bubble */}
          {(e.output_summary || e.error || (!e.input_summary && e.message_preview)) && (
            <div className="flex justify-start">
              <div className="flex gap-3 max-w-[85%]">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-8 h-8 rounded-full bg-gray-800 text-white flex items-center justify-center text-xs font-bold shadow-sm">
                    {e.agent_id ? e.agent_id.substring(0, 2).toUpperCase() : 'AI'}
                  </div>
                </div>
                <div className={`px-4 py-3 rounded-2xl rounded-tl-sm shadow-sm border ${e.success === false ? 'bg-red-50 border-red-200 text-red-900' : 'bg-white border-gray-200 text-gray-800'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                      {e.agent_id || e.workflow || 'Agent'}
                    </span>
                    <span className="text-[10px] text-gray-400 font-mono">
                      {new Date(e.timestamp).toLocaleTimeString()} {e.duration_ms ? `· ${e.duration_ms}ms` : ''}
                    </span>
                  </div>
                  
                  {e.error ? (
                    <div className="text-sm font-medium">{e.error}</div>
                  ) : (
                    <div className="text-sm whitespace-pre-wrap">{e.output_summary || e.message_preview || 'No response recorded.'}</div>
                  )}

                  {(e.request_id || e.agent_id) ? (
                    <div className="mt-3">
                      <button
                        type="button"
                        className="text-[11px] text-indigo-600 hover:text-indigo-800 hover:underline"
                        onClick={() => onOpenExecutionDetails(e.request_id, e.agent_id)}
                      >
                        Open execution details
                      </button>
                    </div>
                  ) : null}

                  {(() => {
                    const parsed = parseStructuredOutput(e.output_summary || e.message_preview || null);
                    if (!parsed) return null;
                    return (
                      <details className="mt-3 group">
                        <summary className="text-[10px] text-gray-400 cursor-pointer hover:text-gray-600 select-none list-none flex items-center gap-1">
                          <ChevronRight className="h-3 w-3 group-open:rotate-90 transition-transform" />
                          Structured Output
                        </summary>
                        <pre className="mt-2 p-2 bg-gray-50 rounded border text-[10px] font-mono text-gray-700 whitespace-pre-wrap break-all max-h-48 overflow-y-auto">
                          {JSON.stringify(parsed, null, 2)}
                        </pre>
                      </details>
                    );
                  })()}
                  
                  {/* Technical Details Expander */}
                  {(e.tool_name || e.event_name) && (
                    <details className="mt-3 group">
                      <summary className="text-[10px] text-gray-400 cursor-pointer hover:text-gray-600 select-none list-none flex items-center gap-1">
                        <ChevronRight className="h-3 w-3 group-open:rotate-90 transition-transform" />
                        Technical Details
                      </summary>
                      <div className="mt-2 p-2 bg-gray-50 rounded border text-[10px] font-mono text-gray-600 space-y-1">
                        {e.event_name && <div>Event: {e.event_name}</div>}
                        {e.tool_name && <div>Tool: {e.tool_name}</div>}
                        {e.source && <div>Source: {e.source}</div>}
                        {e.app_id && <div>App: {e.app_id}</div>}
                      </div>
                    </details>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function PipelineStory({ events }: { events: PulsarEvent[] }) {
  return (
    <div className="relative pl-6 space-y-6 before:absolute before:inset-0 before:ml-[1.4375rem] before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-gray-200 before:to-transparent">
      {events.map((e, idx) => (
        <div key={e.item_id} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
          {/* Timeline Node */}
          <div className={`flex items-center justify-center w-6 h-6 rounded-full border-2 bg-white shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-sm ${e.success === false ? 'border-red-500' : 'border-emerald-500'}`}>
            <div className={`w-2 h-2 rounded-full ${e.success === false ? 'bg-red-500' : 'bg-emerald-500'}`}></div>
          </div>
          
          {/* Card */}
          <div className="w-[calc(100%-2.5rem)] md:w-[calc(50%-1.5rem)] p-3 rounded-lg border bg-white shadow-sm">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs font-bold text-gray-900">{e.event_name || 'Processing Step'}</div>
              <time className="text-[10px] font-mono text-gray-500">{new Date(e.timestamp).toLocaleTimeString()}</time>
            </div>
            
            <div className="text-[10px] text-gray-500 mb-2 flex flex-wrap gap-2">
              {e.stream && <span className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">{e.stream}</span>}
              {e.source && <span>src: {e.source}</span>}
              {e.duration_ms && <span>{e.duration_ms}ms</span>}
            </div>
            
            {e.error && (
              <div className="text-xs text-red-600 font-medium mb-2 bg-red-50 p-1.5 rounded border border-red-100">
                {e.error}
              </div>
            )}
            
            {e.message_preview && (
              <details className="group/payload">
                <summary className="text-[10px] text-indigo-600 cursor-pointer hover:underline select-none font-medium">
                  View Payload
                </summary>
                <div className="mt-2 text-[10px] font-mono text-gray-700 bg-gray-50 p-2 rounded border max-h-32 overflow-y-auto whitespace-pre-wrap break-all">
                  {e.message_preview}
                </div>
              </details>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export {
  buildFilterParams,
  defaultFilters,
  buildConversationPath,
  buildEventsExportPath,
  buildConversationExportPath,
};
